<script setup lang="ts">
  import { computed, onMounted, ref, watch } from "vue";
  import { useRouter } from "vue-router";
  import AppHeader from "../components/AppHeader.vue";
  import CustomTag from "../components/CustomTag.vue";
  import {
    portalDocumentKindLabels,
    useDocumenten,
    type PortalDocument,
    type PortalDocumentKind,
  } from "../composables/useDocumenten";

  import Panel from "primevue/panel";
  import Select from "primevue/select";
  import IconField from "primevue/iconfield";
  import InputIcon from "primevue/inputicon";
  import InputText from "primevue/inputtext";
  import Button from "primevue/button";

  const router = useRouter();
  const { documenten, loading, error, downloadingId, fetchDocumenten, downloadDocument } = useDocumenten();

  const searchQuery = ref("");
  const kindFilter = ref<"all" | PortalDocumentKind>("all");
  const rows = ref(15);
  const displayCount = ref(rows.value);

  const kindOptions = [
    { label: "Alle documenten", value: "all" },
    { label: "Certificaten", value: "certificaat" },
    { label: "Rapporten", value: "rapport" },
    { label: "Offertes", value: "offerte" },
    { label: "Offertebundels", value: "bundel" },
    { label: "Facturen", value: "factuur" },
    { label: "Hersteldocumenten", value: "herstel" },
  ];

  const kindColors: Record<PortalDocumentKind, string> = {
    certificaat: "voldoet",
    rapport: "nieuw",
    offerte: "aangepast",
    bundel: "aangepast",
    factuur: "afgerond",
    herstel: "bezig",
  };

  const filteredDocumenten = computed<PortalDocument[]>(() => {
    return documenten.value.filter((document) => {
      if (kindFilter.value !== "all" && document.kind !== kindFilter.value) {
        return false;
      }

      const query = searchQuery.value.trim().toLowerCase();
      if (!query) return true;

      return [document.label, document.entityLabel, document.entityDescription || ""].some((value) =>
        value.toLowerCase().includes(query),
      );
    });
  });

  const displayedDocumenten = computed(() => filteredDocumenten.value.slice(0, displayCount.value));
  const hasMore = computed(() => displayCount.value < filteredDocumenten.value.length);

  const loadMore = () => {
    displayCount.value = Math.min(displayCount.value + rows.value, filteredDocumenten.value.length);
  };

  watch(filteredDocumenten, (value) => {
    displayCount.value = Math.min(rows.value, value.length);
  });

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    try {
      return new Intl.DateTimeFormat("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(dateString));
    } catch {
      return "-";
    }
  };

  const openEntity = (document: PortalDocument) => {
    router.push(document.entityPath);
  };

  onMounted(() => {
    void fetchDocumenten();
  });
</script>

<template>
  <div class="documenten-page">
    <AppHeader
      title="Documenten"
      :show-back-button="true"
      back-to="/"
      back-text="Terug naar dashboard"
    />

    <Panel
      class="filters-card"
      toggleable
      collapsed
      header="Zoeken en Filteren"
    >
      <div class="filters-stack">
        <div class="filters-search">
          <IconField>
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>
            <InputText
              v-model="searchQuery"
              placeholder="Zoek op document, nummer of locatie..."
              class="filters-input"
            />
          </IconField>
          <div
            v-if="searchQuery && filteredDocumenten.length !== undefined"
            class="filters-meta"
          >
            {{ filteredDocumenten.length }} resultaten gevonden
          </div>
        </div>

        <div class="filters-row">
          <div class="filters-select">
            <Select
              v-model="kindFilter"
              :options="kindOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Filter op type"
              class="w-full"
            />
          </div>

          <Button
            icon="pi pi-refresh"
            @click="fetchDocumenten()"
            :loading="loading"
            severity="secondary"
            outlined
            aria-label="Vernieuwen"
            class="filters-refresh"
          />
        </div>
      </div>
    </Panel>

    <div
      v-if="loading"
      class="status-card status-loading"
    >
      <i class="pi pi-spin pi-spinner text-4xl"></i>
    </div>

    <div
      v-else-if="error"
      class="status-card status-error"
    >
      {{ error }}
    </div>

    <div
      v-else-if="filteredDocumenten.length === 0"
      class="status-card status-empty"
    >
      <template v-if="searchQuery || kindFilter !== 'all'"> Geen documenten gevonden voor deze filters. </template>
      <template v-else> Er zijn nog geen documenten beschikbaar. </template>
    </div>

    <div v-else>
      <div class="documenten-header">
        <span class="documenten-count">{{ filteredDocumenten.length }} documenten gevonden</span>
        <Button
          icon="pi pi-refresh"
          @click="fetchDocumenten()"
          :loading="loading"
          severity="secondary"
          outlined
          aria-label="Vernieuwen"
          size="small"
        />
      </div>

      <div class="documenten-list">
        <div
          v-for="document in displayedDocumenten"
          :key="document.id"
          class="document-card"
        >
          <div class="document-icon">
            <i class="pi pi-file-pdf" />
          </div>

          <div class="document-main">
            <div class="document-title-row">
              <span class="document-title">{{ document.label }}</span>
              <CustomTag
                :color="kindColors[document.kind]"
                :label="portalDocumentKindLabels[document.kind]"
              />
            </div>
            <div class="document-meta">
              <span class="document-date">{{ formatDate(document.date) }}</span>
              <span
                v-if="document.entityDescription"
                class="document-description"
              >
                {{ document.entityDescription }}
              </span>
            </div>
          </div>

          <div class="document-actions">
            <Button
              :label="document.entityLabel"
              severity="secondary"
              text
              size="small"
              icon="pi pi-arrow-up-right"
              @click="openEntity(document)"
            />
            <Button
              label="Download"
              severity="secondary"
              outlined
              size="small"
              icon="pi pi-download"
              :loading="downloadingId === document.id"
              @click="downloadDocument(document)"
            />
          </div>
        </div>
      </div>

      <div
        v-if="hasMore"
        class="documenten-load-more"
        @click="loadMore"
      >
        <span class="documenten-load-more-text"> <i class="pi pi-arrow-down" /> Meer laden... </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .documenten-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding-bottom: 2rem;
  }

  .filters-card {
    background: transparent;
    border-radius: 0;
    border: none;
    box-shadow: none;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--p-background-200);
  }

  .filters-card :deep(.p-panel-header) {
    background: transparent;
    border-bottom: none;
    font-weight: 600;
    color: var(--p-background-800);
    padding: 0 0 0.75rem;
  }

  .filters-card :deep(.p-panel-content) {
    background: transparent;
    padding: 0;
  }

  .filters-card :deep(.p-panel-toggleable) {
    padding: 0;
  }

  .filters-stack {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .filters-search {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .filters-input {
    width: 100%;
  }

  .filters-meta {
    font-size: 0.85rem;
    color: var(--p-background-600);
  }

  .filters-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
    padding-top: 0.75rem;
    border-top: 1px solid var(--p-background-200);
  }

  .filters-select {
    flex: 1;
    min-width: 220px;
  }

  .filters-refresh {
    margin-left: auto;
  }

  .status-card {
    border-radius: var(--radius-sm);
    border: 1px solid var(--p-background-200);
    background: var(--p-background-0);
    padding: 1.25rem;
    text-align: center;
    color: var(--p-background-600);
  }

  .status-loading {
    background: var(--p-background-0);
  }

  .status-error {
    border-color: var(--p-red-200);
    background: var(--p-red-50);
    color: var(--p-red-600);
  }

  .documenten-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--p-background-200);
    margin-bottom: 1.25rem;
  }

  .documenten-count {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--p-background-600);
  }

  .documenten-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .document-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    padding: 1rem 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .document-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    flex-shrink: 0;
    border-radius: 9999px;
    background: var(--p-background-100);
    color: var(--p-background-600);
    font-size: 1.15rem;
  }

  .document-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .document-title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .document-title {
    font-weight: 600;
    color: var(--p-background-900);
    word-break: break-word;
  }

  .document-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 0.75rem;
    font-size: 0.85rem;
    color: var(--p-background-600);
  }

  .document-date {
    white-space: nowrap;
  }

  .document-description {
    word-break: break-word;
  }

  .document-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .documenten-load-more {
    margin-top: 1rem;
    padding: 0.75rem 0;
    border-top: 1px solid var(--p-background-200);
    text-align: center;
    cursor: pointer;
    color: var(--p-background-600);
    transition: color 0.2s ease;
  }

  .documenten-load-more:hover {
    color: var(--p-primary-500);
  }

  .documenten-load-more-text {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
  }

  @media (max-width: 640px) {
    .filters-refresh {
      margin-left: 0;
    }

    .document-card {
      flex-direction: column;
      align-items: stretch;
    }

    .document-actions {
      justify-content: flex-start;
    }
  }
</style>
